import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, map, catchError, throwError } from 'rxjs';
import { EnvironmentService } from '../shared/environment.service';

/**
 * Feedback categories matching the proto FeedbackCategory enum.
 */
export type FeedbackCategory = 'BUG' | 'FEATURE_REQUEST' | 'QUESTION' | 'OTHER';

/**
 * Request to submit feedback (mirrors SubmitFeedbackRequest proto).
 */
export interface SubmitFeedbackRequest {
  description: string;
  category: FeedbackCategory;
  chatSessionId?: string;
  chatLogJson?: string;
  pageUrl: string;
  uiStateJson?: string;
  userEmail?: string;
}

/**
 * Response from feedback submission (mirrors SubmitFeedbackResponse proto).
 */
export interface SubmitFeedbackResponse {
  issueUrl: string;
  issueNumber: number;
  enhancedDescription: string;
}

/**
 * Service for in-app feedback / issue reporting (Issue #577).
 *
 * Communicates with the backend FeedbackService via the gRPC-gateway REST API.
 */
@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);

  private readonly API_BASE: string;

  constructor() {
    this.API_BASE = `https://${this.env.getApiServer()}/v1/feedback`;
    ClientLoggingService.log('INFO', '[FeedbackService] API endpoint:', this.API_BASE);
  }

  /**
   * Submit feedback / report a problem.
   * The backend enhances the description via LLM and creates a GitHub issue.
   */
  submitFeedback(request: SubmitFeedbackRequest): Observable<SubmitFeedbackResponse> {
    // TODO(Issue #577): This manual mapping is brittle. Ideally, the frontend should
    // use generated constants or the proto schema should handle this mapping logic.
    const CATEGORY_MAP: Record<FeedbackCategory, string> = {
      BUG: 'FEEDBACK_CATEGORY_BUG',
      FEATURE_REQUEST: 'FEEDBACK_CATEGORY_FEATURE_REQUEST',
      QUESTION: 'FEEDBACK_CATEGORY_QUESTION',
      OTHER: 'FEEDBACK_CATEGORY_OTHER',
    };

    // Map to proto field names (snake_case for gRPC-gateway JSON transcoding)
    const body = {
      description: request.description,
      category: CATEGORY_MAP[request.category],
      chat_session_id: request.chatSessionId || '',
      chat_log_json: request.chatLogJson || '',
      page_url: request.pageUrl,
      ui_state_json: request.uiStateJson || '',
      user_email: request.userEmail || '',
    };

    return this.http.post<any>(this.API_BASE, body).pipe(
      map(resp => ({
        issueUrl: resp.issueUrl ?? resp.issue_url,
        issueNumber: resp.issueNumber ?? resp.issue_number,
        enhancedDescription: resp.enhancedDescription ?? resp.enhanced_description ?? request.description ?? '',
      })),
      catchError((error: HttpErrorResponse) => {
        const serverMessage = this.extractServerMessage(error);
        ClientLoggingService.log('ERROR', `[FeedbackService] Submit failed (HTTP ${error.status}):`, serverMessage);
        return throwError(() => ({
          status: error.status,
          message: serverMessage,
          raw: error,
        }));
      })
    );
  }

  /**
   * Extracts a human-readable error message from gRPC-gateway error responses.
   * gRPC-gateway returns `{"message": "...", "code": N}` for gRPC errors.
   */
  private extractServerMessage(error: HttpErrorResponse): string {
    if (error.status === 0) {
      return 'Unable to reach the server. Please check your connection and try again.';
    }
    const body = error.error;
    if (typeof body === 'object' && body?.message) {
      return body.message;
    }
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (parsed?.message) return parsed.message;
      } catch {
        // not JSON
      }
      return body;
    }
    return `Server error (HTTP ${error.status}). Please try again.`;
  }
}
